import React, { useState, useEffect, useMemo } from 'react';
import { Color, Palette } from '../../types/ColorLibraryModel';
import { isColorDark } from '../../utils/color-library-utils';
// import Tooltip from '../general/Tooltip';

interface ColorSquareProps {
  color: Color;
  selectedColors: Color[];
  selectedColor?: Color | null;
  palettes?: Palette[];
  hideColorAddition?: boolean;
  showRemoveButton?: boolean;
  showCreatePaletteText?: boolean;
  clickedPalette?: Palette | null;
  onToggleColor?: (color: Color) => void;
  onAddColorToPalette?: (palette: Palette) => void;
  onRemoveColorFromPalette?: (colorId: string) => void;
  onDownloadColor?: (colors: Color[]) => void;
  onClick?: (color: Color) => void;
}

const ColorSquare: React.FC<ColorSquareProps> = ({ color, selectedColors, onToggleColor, onClick }) => {
  const [windowWidth, setWindowWidth] = useState(1440);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const isSelected = useMemo(() => selectedColors.some((c) => c.id === color.id), [selectedColors, color.id]);
  const isDark = useMemo(() => isColorDark(color.hexCode), [color.hexCode]);

  const handleToggleSelection = () => {
    if (onToggleColor) {
      onToggleColor(color);
    } else if (onClick) {
      onClick(color);
    }
  };
  const getEffectClass = () => {
    if (color.effect === 'Metallic') return 'bg-metallic';
    if (color.effect === 'Lustre') return 'bg-lustre';
    if (color.effect === 'Chrome') return 'bg-chrome';
    return '';
  };

  const isMobile = windowWidth < 768;

  return (
    <div className="group relative min-w-[15%] hover:z-10 md:min-w-[5%]">
      <div
        className={`cursor-pointer transition-all duration-200 ${getEffectClass()}`}
        style={{
          backgroundColor: `#${color.hexCode}`,
          height: '200px',
          border: isSelected ? `2px solid ${isDark ? 'white' : 'black'}` : 'none',
          boxSizing: 'border-box',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
        }}
        onClick={isMobile ? () => onClick?.(color) : handleToggleSelection}
      />

      {/* Tooltip - only show on desktop */}
      {!isMobile && (
        <div
          className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 origin-bottom -translate-x-1/2 scale-0 transform rounded px-3 py-2 text-xs whitespace-nowrap text-white opacity-0 shadow-xl transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:scale-100 group-hover:opacity-100"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          {color.displayName}
        </div>
      )}
    </div>
  );
};

export default React.memo(ColorSquare);
